/**
 * Validates and logs the form payload before sending to API
 * Useful for debugging and ensuring all properties are included
 */
export const validateFormPayload = (payload: any): boolean => {
  const errors: string[] = [];

  // Required fields
  if (!payload.title) errors.push('Title is required');
  if (!payload.handler) errors.push('Handler is required');
  if (!payload.fields || !Array.isArray(payload.fields)) {
    errors.push('Fields array is required');
  }

  // Validate each field
  if (payload.fields && Array.isArray(payload.fields)) {
    payload.fields.forEach((field: any, index: number) => {
      if (!field.id) errors.push(`Field ${index}: id is required`);
      if (!field.type) errors.push(`Field ${index}: type is required`);
      if (!field.label) errors.push(`Field ${index}: label is required`);
      if (!field.name) errors.push(`Field ${index}: name is required`);
      if (field.order === undefined) errors.push(`Field ${index}: order is required`);

      // Validate conditional logic if present
      if (field.conditionalLogic && Array.isArray(field.conditionalLogic)) {
        field.conditionalLogic.forEach((logic: any, logicIndex: number) => {
          if (!logic.field) errors.push(`Field ${index}, Condition ${logicIndex}: field is required`);
          if (!logic.operator) errors.push(`Field ${index}, Condition ${logicIndex}: operator is required`);
          if (!logic.action) errors.push(`Field ${index}, Condition ${logicIndex}: action is required`);
        });
      }

      // Validate calculation if present
      if (field.calculation) {
        if (!field.calculation.formula) errors.push(`Field ${index}: calculation formula is required`);
        if (!field.calculation.fields || !Array.isArray(field.calculation.fields)) {
          errors.push(`Field ${index}: calculation fields array is required`);
        }
      }

      // Validate options for select/radio/checkbox
      if (['select', 'radio', 'checkbox'].includes(field.type)) {
        if (!field.options || !Array.isArray(field.options) || field.options.length === 0) {
          errors.push(`Field ${index}: options are required for ${field.type} type`);
        }
      }
    });
  }

  if (errors.length > 0) {
    console.error('Payload validation errors:', errors);
    return false;
  }

  console.log('✅ Payload validation passed');
  console.log('Payload structure:', JSON.stringify(payload, null, 2));
  return true;
};

/**
 * Logs the complete payload structure for debugging
 */
export const logPayloadStructure = (payload: any): void => {
  console.group('📤 Form API Payload');
  console.log('Title:', payload.title);
  console.log('Handler:', payload.handler);
  console.log('Description:', payload.description);
  console.log('Medical Category:', payload.medical_category);
  console.log('Total Fields:', payload.fields?.length || 0);
  
  if (payload.fields && payload.fields.length > 0) {
    console.group('Fields:');
    payload.fields.forEach((field: any, index: number) => {
      console.group(`Field ${index + 1}: ${field.label} (${field.type})`);
      console.log('ID:', field.id);
      console.log('Name:', field.name);
      console.log('Required:', field.required);
      console.log('Order:', field.order);
      
      if (field.placeholder) console.log('Placeholder:', field.placeholder);
      if (field.defaultValue) console.log('Default Value:', field.defaultValue);
      if (field.helpText) console.log('Help Text:', field.helpText);
      if (field.width) console.log('Width:', field.width);
      
      if (field.validation && Object.keys(field.validation).length > 0) {
        console.log('Validation:', field.validation);
      }
      
      if (field.options && field.options.length > 0) {
        console.log('Options:', field.options);
      }
      
      if (field.conditionalLogic && field.conditionalLogic.length > 0) {
        console.log('Conditional Logic:', field.conditionalLogic);
      }
      
      if (field.calculation) {
        console.log('Calculation:', field.calculation);
      }
      
      console.groupEnd();
    });
    console.groupEnd();
  }
  
  console.groupEnd();
};
